/*
    User-facing text out of a server answer.

    The backend writes its diagnostics into the very string the user reads —
    «Не піднято пістолет. error: 2, status: 48» — and the pair means nothing
    to a driver at the pump. The codes are still read separately by the flow
    (see resolveFailure in the RefuelModal); only the text loses them here.
 */

/** Letters and digits the boundary checks below treat as part of a word. */
const WORD = 'A-Za-z0-9А-Яа-яЁёІіЇїЄєҐґ'

/*
    One diagnostic: a keyword, an optional separator, a number — `error: 2`,
    `status:48`, `status^ 48`, `Status=5`, `код 48`, `помилка №2`. The leading
    group keeps the character before it, because `\b` knows no Cyrillic; the
    trailing lookahead stops `код 2` from eating into `код 2а`.
 */
const CODE = new RegExp(
  `(^|[^${WORD}])(?:error|err|status|code|помилка|ошибка|статус|код)\\s*[:=^#№-]?\\s*\\d+(?![${WORD}])`,
  'gi'
)

/** Brackets a stripped diagnostic leaves behind: `()`, `[ , ]`, `{ ; }`. */
const EMPTY_BRACKETS = /[([{]\s*[,;:/|\-–—]*\s*[)\]}]/g

/**
 * The text minus its technical codes, tidied so nothing dangles where they
 * stood. `undefined` when nothing readable is left — the caller then falls
 * back to its own wording, never to the codes.
 */
export const stripTechnicalCodes = (text?: string): string | undefined => {
  if (!text) return undefined
  const clean = text
    .replace(CODE, '$1')
    .replace(EMPTY_BRACKETS, '')
    // Two separators that used to have a code between them.
    .replace(/([,;:/|\-–—])(\s*[,;:/|\-–—])+/g, '$1')
    // A separator right before a full stop — «Не піднято пістолет, .»
    .replace(/\s*[,;:/|\-–—]+\s*(?=[.!?])/g, '')
    // No space in front of punctuation, one space anywhere else.
    .replace(/\s+([.,;:!?)\]}])/g, '$1')
    .replace(/\s{2,}/g, ' ')
    // Separators left at either end.
    .replace(/^[\s,;:.!?/|\-–—]+/, '')
    .replace(/[\s,;:/|\-–—]+$/, '')
    .trim()
  return clean || undefined
}
